import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import "../CSS/Navbar.css";
import { useAuth } from "../context/AuthContext";

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const navbarRef = useRef<HTMLDivElement>(null);
  const adsMenuRef = React.useRef<Menu>(null);
  const carsMenuRef = React.useRef<Menu>(null);
  const blogsMenuRef = React.useRef<Menu>(null);
  const realEstateMenuRef = React.useRef<Menu>(null);

  const { logout, isAuthenticated } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        navbarRef.current &&
        !navbarRef.current.contains(event.target as Node)
      ) {
        setActiveMenu(null);
        setIsMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActiveMenu(null);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    setActiveMenu(null);
    setIsMenuOpen(false);
  }, [location]);

  const handleSignOut = async () => {
    try {
      logout();
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleMenuClick = (e: React.MouseEvent, menuName: string) => {
    e.preventDefault();
    e.stopPropagation();

    const menuRef = getMenuRef(menuName);
    if (menuRef?.current) {
      if (activeMenu === menuName) {
        menuRef.current.hide(e);
        setActiveMenu(null);
      } else {
        // Hide previous menu if any
        if (activeMenu) {
          const prevMenuRef = getMenuRef(activeMenu);
          prevMenuRef?.current?.hide(e);
        }
        menuRef.current.toggle(e);
        setActiveMenu(menuName);
      }
    }
  };

  const handleMenuItemClick = (route: string, menuName: string) => {
    navigate(route);
    setIsMenuOpen(false);
    setActiveMenu(null);
  };

  const getMenuRef = (menuName: string) => {
    switch (menuName) {
      case "ads":
        return adsMenuRef;
      case "cars":
        return carsMenuRef;
      case "blogs":
        return blogsMenuRef;
      case "realEstate":
        return realEstateMenuRef;
      default:
        return null;
    }
  };

  const adsItems = [
    {
      label: "Lebanon Ads",
      icon: "pi pi-list",
      command: () => handleMenuItemClick("/lebanon-ads", "ads"),
    },
    {
      label: "Abidjan Ads",
      icon: "pi pi-list",
      command: () => handleMenuItemClick("/abidjan-ads", "ads"),
    },
    {
      label: "Add New Ad",
      icon: "pi pi-plus",
      command: () => handleMenuItemClick("/add-ads", "ads"),
      visible: isAuthenticated,
    },
  ];

  const carsItems = [
    {
      label: "Lebanon Cars",
      icon: "pi pi-list",
      command: () => handleMenuItemClick("/lebanon-cars", "cars"),
    },
    {
      label: "Abidjan Cars",
      icon: "pi pi-list",
      command: () => handleMenuItemClick("/abidjan-cars", "cars"),
    },
    {
      label: "Add New Car",
      icon: "pi pi-plus",
      command: () => handleMenuItemClick("/add-car", "cars"),
      visible: isAuthenticated,
    },
  ];

  const blogsItems = [
    {
      label: "View Blogs",
      icon: "pi pi-list",
      command: () => handleMenuItemClick("/blog", "blogs"),
    },
    {
      label: "Add New Blog",
      icon: "pi pi-plus",
      command: () => handleMenuItemClick("/add-blog", "blogs"),
      visible: isAuthenticated,
    },
  ];

  const realEstateItems = [
    {
      label: "Lebanon Properties",
      icon: "pi pi-list",
      command: () => handleMenuItemClick("/lebanon-realestate", "realEstate"),
    },
    {
      label: "Abidjan Properties",
      icon: "pi pi-list",
      command: () => handleMenuItemClick("/abidjan-realestate", "realEstate"),
    },
    {
      label: "Add Property",
      icon: "pi pi-plus",
      command: () => handleMenuItemClick("/add-realestate", "realEstate"),
      visible: isAuthenticated,
    },
  ];

  return (
    <nav className="navbar" ref={navbarRef}>
      <div className="navbar-brand">
        <Link to="/">
          <img
            src={process.env.PUBLIC_URL + "/logoic.png"}
            alt="taswik logo"
            className="navbar-logo"
          />
        </Link>
      </div>
      <Button
        icon="pi pi-bars"
        className="p-button-text hamburger-menu"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-label="Menu"
        aria-expanded={isMenuOpen}
      />

      <div
        className={`navbar-links ${isMenuOpen ? "active" : ""}`}
        role="navigation"
      >
        <Link to="/" onClick={() => setIsMenuOpen(false)}>
          <Button
            label="Home"
            className={`p-button-text ${
              location.pathname === "/" ? "active-link" : ""
            }`}
            icon="pi pi-home"
          />
        </Link>
        <Link to="/about" onClick={() => setIsMenuOpen(false)}>
          <Button
            label="About"
            className={`p-button-text ${
              location.pathname === "/about" ? "active-link" : ""
            }`}
            icon="pi pi-info-circle"
          />
        </Link>

        <div className="dropdown-button">
          <Button
            label="Ads"
            icon="pi pi-images"
            className={`p-button-text ${
              location.pathname === "/lebanon-ads" ||
              location.pathname === "/abidjan-ads" ||
              location.pathname === "/add-ads" ||
              location.pathname.startsWith("/edit-ads")
                ? "active-link"
                : ""
            }`}
            onClick={(e) => handleMenuClick(e, "ads")}
            aria-controls="ads-menu"
            aria-haspopup
            aria-expanded={activeMenu === "ads"}
          />
          <Menu
            model={adsItems}
            popup
            ref={adsMenuRef}
            id="ads-menu"
            onHide={() => setActiveMenu(null)}
            className="navbar-dropdown-menu"
          />
        </div>

        <div className="dropdown-button">
          <Button
            label="Cars"
            icon="pi pi-car"
            className={`p-button-text ${
              location.pathname === "/lebanon-cars" ||
              location.pathname === "/abidjan-cars" ||
              location.pathname === "/add-car" ||
              location.pathname.startsWith("/edit-car")
                ? "active-link"
                : ""
            }`}
            onClick={(e) => handleMenuClick(e, "cars")}
            aria-controls="cars-menu"
            aria-haspopup
            aria-expanded={activeMenu === "cars"}
          />
          <Menu
            model={carsItems}
            popup
            ref={carsMenuRef}
            id="cars-menu"
            onHide={() => setActiveMenu(null)}
            className="navbar-dropdown-menu"
          />
        </div>

        <div className="dropdown-button">
          <Button
            label="Real Estate"
            icon="pi pi-building"
            className={`p-button-text ${
              location.pathname === "/lebanon-realestate" ||
              location.pathname === "/abidjan-realestate" ||
              location.pathname === "/add-realestate" ||
              location.pathname.startsWith("/edit-realestate")
                ? "active-link"
                : ""
            }`}
            onClick={(e) => handleMenuClick(e, "realEstate")}
            aria-controls="realEstate-menu"
            aria-haspopup
            aria-expanded={activeMenu === "realEstate"}
          />
          <Menu
            model={realEstateItems}
            popup
            ref={realEstateMenuRef}
            id="realEstate-menu"
            onHide={() => setActiveMenu(null)}
            className="navbar-dropdown-menu"
          />
        </div>

        <div className="dropdown-button">
          <Button
            label="Blogs"
            icon="pi pi-book"
            className={`p-button-text ${
              location.pathname === "/blog" ||
              location.pathname === "/add-blog" ||
              location.pathname.startsWith("/edit-blog")
                ? "active-link"
                : ""
            }`}
            onClick={(e) => handleMenuClick(e, "blogs")}
            aria-controls="blogs-menu"
            aria-haspopup
            aria-expanded={activeMenu === "blogs"}
          />
          <Menu
            model={blogsItems}
            popup
            ref={blogsMenuRef}
            id="blogs-menu"
            onHide={() => setActiveMenu(null)}
            className="navbar-dropdown-menu"
          />
        </div>

        {isAuthenticated && (
          <Button
            label="Sign Out"
            className="p-button-danger p-button-text"
            icon="pi pi-sign-out"
            onClick={handleSignOut}
          />
        )}
      </div>
    </nav>
  );
};

export default Navbar;
